'use client';

import { useIsBelowBreakpoint } from '@morpho-org/ui-v6/hooks';
import { cn } from '@morpho-org/ui-v6/utils';
import { Button, Icon, Separator, Tooltip, TooltipContent, TooltipTrigger } from '@repo/ui';
import { Close, Filter, Search } from '@repo/ui/icons';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Command } from 'cmdk';
import { Popover as RadixPopover } from 'radix-ui';
import { type ReactNode, use, useState } from 'react';
import type { FilterItem } from '../AdvancedFilter/AdvancedFilter';
import type { MasterFilterState } from './types';
import AdvancedFilterItem from '../AdvancedFilter/AdvancedFilterItem';
import { FilterRow } from './FilterRow';
import { MasterFilterContext } from './MasterFilter';

// Fallback when header hasn't been measured yet
const DEFAULT_HEADER_HEIGHT = 45;
export interface MasterFilterAdvancedSelectProps<TValue, TState extends MasterFilterState = MasterFilterState> {
  name: Extract<keyof TState, string>;
  label: string;
  items: FilterItem<TValue>[];
  tooltip?: string;
  placeholder?: string;
  searchPlaceholder?: string;
  slot?: ReactNode;
  className?: string;
}
export function MasterFilterAdvancedSelect<TValue, TState extends MasterFilterState = MasterFilterState>({
  name,
  label,
  items,
  tooltip,
  placeholder = 'All',
  searchPlaceholder = 'Search...',
  slot,
  className
}: MasterFilterAdvancedSelectProps<TValue, TState>) {
  const context = use(MasterFilterContext);
  const isMobile = useIsBelowBreakpoint(768);
  const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(null);
  const [headerHeight, setHeaderHeight] = useState(DEFAULT_HEADER_HEIGHT);
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [sortedIds, setSortedIds] = useState<string[]>([]);
  if (!context) {
    throw new Error('MasterFilter.AdvancedSelect must be used within a MasterFilter.Provider');
  }
  const {
    state,
    actions
  } = context;
  // oxlint-disable-next-line typescript/consistent-type-assertions
  const rawSelected = state[name as string];
  // oxlint-disable-next-line typescript/consistent-type-assertions
  const selected = Array.isArray(rawSelected) ? rawSelected as TValue[] : [];

  // Filter items by search
  const filteredItems = search ? items.filter(item => {
    const searchLower = search.toLowerCase();

    // Search in label
    if (item.label.toLowerCase().includes(searchLower)) {
      return true;
    }

    // Search in searchableFields if present
    if (item.searchableFields) {
      return item.searchableFields.some(field => field.toLowerCase().includes(searchLower));
    }
    return false;
  }) : items;

  // Update items with isChecked, using stored sort order from when popover opened
  const itemsWithChecked = filteredItems.map(item_0 => ({
    ...item_0,
    isChecked: selected.includes(item_0.value)
  })).toSorted((a, b) => sortedIds.indexOf(a.id) - sortedIds.indexOf(b.id));
  const virtualizer = useVirtualizer({
    count: itemsWithChecked.length,
    getScrollElement: () => scrollElement,
    estimateSize: () => 33,
    // 29px item + 4px gap
    overscan: 5,
    getItemKey: index => itemsWithChecked[index]!.id
  });

  // Display text logic — only count selected values that exist in current items
  const visibleSelected = selected.filter(value => items.some(i => i.value === value));
  const displayText = (() => {
    if (visibleSelected.length === 0) return placeholder;
    const labels = visibleSelected.slice(0, 2).map(value_0 => items.find(i_0 => i_0.value === value_0)?.label).filter(Boolean);
    if (visibleSelected.length <= 2) {
      return labels.join(', ') || `${visibleSelected.length} selected`;
    }
    const remaining = visibleSelected.length - 2;
    return `${labels.join(', ')}, and ${remaining} more`;
  })();
  const handleToggle = (value_1: TValue) => {
    const newValue = selected.includes(value_1) ? selected.filter(v => v !== value_1) : [...selected, value_1];
    // oxlint-disable-next-line typescript/consistent-type-assertions
    actions.updateField(name, newValue as (typeof state)[typeof name]);
  };
  const handleClear = () => {
    // oxlint-disable-next-line typescript/consistent-type-assertions
    actions.updateField(name, [] as unknown as (typeof state)[typeof name]);
  };
  const handleClearClick = (event: React.MouseEvent<HTMLSpanElement>) => {
    event.stopPropagation(); // Prevent dropdown from opening
    handleClear();
  };
  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open) {
      // Compute sort order when opening: selected first, then alphabetically
      const sorted = [...items].toSorted((a_0, b_0) => {
        const aSelected = selected.includes(a_0.value);
        const bSelected = selected.includes(b_0.value);
        if (aSelected !== bSelected) return aSelected ? -1 : 1;
        return a_0.label.localeCompare(b_0.label);
      });
      setSortedIds(sorted.map(item_1 => item_1.id));
    } else {
      setSearch('');
    }
  };
  return <FilterRow label={label} tooltip={tooltip} className={className}>
      <RadixPopover.Root open={isOpen} onOpenChange={handleOpenChange} modal>
        {context.meta.contentElement && <RadixPopover.Anchor virtualRef={{
        current: context.meta.contentElement
      }} />}
        <RadixPopover.Trigger asChild>
          <Button variant="ghost" size="sm" data-testid={`advanced-select-${name}`} className={cn('min-w-0 shrink overflow-hidden', selected.length > 0 && 'text-foreground', isOpen && 'bg-accent')}>
            <Filter />
            <span className="truncate">{displayText}</span>
            {selected.length > 0 ? <Tooltip>
                <TooltipTrigger asChild>
                  <span onClick={handleClearClick} role="button" className={cn('flex cursor-pointer items-center border-none bg-transparent p-0', 'text-muted-foreground hover:text-foreground', 'transition-colors duration-150')}>
                    <Close />
                  </span>
                </TooltipTrigger>
                {/* Raise above the open filter popover (`z-60`); tooltips default to `z-50`. */}
                <TooltipContent className="z-60">Clear selection</TooltipContent>
              </Tooltip> : null}
          </Button>
        </RadixPopover.Trigger>
        <RadixPopover.Portal>
          <RadixPopover.Content side={isMobile ? 'bottom' : 'right'} align="start" sideOffset={isMobile ? -(context.meta.contentElement?.offsetHeight ?? 0) : 4} collisionPadding={15} onInteractOutside={event_0 => {
          // On mobile, use default Radix modal behavior (dismiss one at a time)
          if (isMobile) return;
          const el = context.meta.contentElement;
          if (!el) return;
          const originalEvent = event_0.detail.originalEvent;
          if (!('clientX' in originalEvent)) return;
          // oxlint-disable-next-line typescript/consistent-type-assertions
          const {
            clientX,
            clientY
          } = originalEvent as PointerEvent;
          const rect = el.getBoundingClientRect();
          const isInsideMain = clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom;
          if (!isInsideMain) {
            context.meta.setIsOpen(false);
          }
        }} style={{
          maxHeight: context.meta.contentElement?.offsetHeight,
          ...(isMobile && {
            width: context.meta.contentElement?.offsetWidth
          })
        }} className={cn('flex flex-col', !isMobile && 'w-[280px]', 'bg-popover overflow-hidden rounded-lg', 'border-[0.5px] border-border', 'shadow-lg z-60', 'data-[state=open]:animate-slide-down-and-fade')}>
            <Command shouldFilter={false} defaultValue="-">
              {/* Header: search + separator + slot — measured to compute scroll area */}
              <div ref={el_0 => {
              if (el_0) setHeaderHeight(el_0.offsetHeight);
            }}>
                <div className="gap-sm pt-sm px-sm pb-xs flex items-center">
                  <div className="flex w-full items-center gap-[2px]">
                    <Icon icon={<Search />} className="text-muted-foreground" />
                    <Command.Input placeholder={searchPlaceholder} value={search} onValueChange={setSearch} className={cn('w-full border-none bg-transparent outline-none', 'text-foreground placeholder:text-muted-foreground', 'text-2xs placeholder:text-2xs leading-none placeholder:leading-none')} />
                  </div>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="ghost" size="sm" disabled={selected.length === 0} onClick={handleClear}>
                        Clear
                      </Button>
                    </TooltipTrigger>
                    {/* Raise above the open filter popover (`z-60`); tooltips default to `z-50`. */}
                    {selected.length !== 0 && <TooltipContent className="z-60">Clear selection</TooltipContent>}
                  </Tooltip>
                </div>
                <Separator />
                {slot}
              </div>
              <Command.List className="overflow-hidden">
                <div ref={setScrollElement} style={{
                maxHeight: context.meta.contentElement ? context.meta.contentElement.offsetHeight - headerHeight : 240
              }} className={cn('overflow-x-hidden overflow-y-auto', 'p-2xs')}>
                  <Command.Group>
                    <div style={{
                    height: virtualizer.getTotalSize(),
                    position: 'relative'
                  }}>
                      {virtualizer.getVirtualItems().map(virtualRow => {
                      const item_2 = itemsWithChecked[virtualRow.index]!;
                      return <div key={item_2.id} style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        transform: `translateY(${virtualRow.start}px)`
                      }}>
                            <AdvancedFilterItem<TValue> item={item_2} toggleItem={handleToggle} />
                          </div>;
                    })}
                    </div>
                  </Command.Group>
                </div>
              </Command.List>
            </Command>
          </RadixPopover.Content>
        </RadixPopover.Portal>
      </RadixPopover.Root>
    </FilterRow>;
}
MasterFilterAdvancedSelect.displayName = 'MasterFilterAdvancedSelect';